<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
    modelValue: { type: String, default: '' },
    filterType: { type: String, default: null },
    placeholder: { type: String, default: 'Select a field...' },
})

const emit = defineEmits(['update:modelValue'])

const search = ref('')
const isOpen = ref(false)
const fieldGroups = ref([])
const inputRef = ref(null)

onMounted(async () => {
    try {
        const response = await fetch('/bo/communications/api/v1/workflows/field-definitions')
        const result = await response.json()
        fieldGroups.value = result.data || []
    } catch (e) {
        console.error('Failed to load field definitions', e)
    }
})

const filteredGroups = computed(() => {
    const query = search.value.toLowerCase()
    return fieldGroups.value
        .map(group => ({
            ...group,
            fields: group.fields.filter(f => {
                const matchesSearch = !query || f.title.toLowerCase().includes(query) || f.key.toLowerCase().includes(query) || group.category.toLowerCase().includes(query)
                const matchesType = !props.filterType || f.type === props.filterType
                return matchesSearch && matchesType
            }),
        }))
        .filter(group => group.fields.length > 0)
})

// Fields the engine can't read for a contact yet are listed but blocked;
// publishing rejects them too (GraphValidator / WorkflowFieldCatalog).
function isUnavailable(field) {
    return field.available === false
}

const selectedField = computed(() => {
    if (!props.modelValue) return null
    for (const group of fieldGroups.value) {
        const found = group.fields.find(f => f.key === props.modelValue)
        if (found) return found
    }
    return null
})

const hasUnavailableFields = computed(() =>
    filteredGroups.value.some(group => group.fields.some(isUnavailable))
)

const selectedFieldTitle = computed(() => {
    if (!props.modelValue) return ''
    for (const group of fieldGroups.value) {
        const found = group.fields.find(f => f.key === props.modelValue)
        if (found) return found.title
    }
    return props.modelValue
})

function selectField(field) {
    if (isUnavailable(field)) return
    emit('update:modelValue', field.key)
    isOpen.value = false
    search.value = ''
}

function openDropdown() {
    isOpen.value = true
    search.value = ''
    setTimeout(() => inputRef.value?.focus(), 50)
}

function handleBlur() {
    setTimeout(() => { isOpen.value = false }, 200)
}

const typeIcons = { date: '📅', number: '#', text: 'T', textarea: 'T', dropdown: '▼' }
</script>

<template>
    <div class="field-picker">
        <button
            v-if="!isOpen"
            class="field-picker__trigger"
            type="button"
            @click="openDropdown"
        >
            <span v-if="selectedFieldTitle">{{ selectedFieldTitle }}</span>
            <span v-else class="field-picker__placeholder">{{ placeholder }}</span>
        </button>
        <p v-if="!isOpen && selectedField && isUnavailable(selectedField)" class="field-picker__warning">
            This field is not available yet. Pick another field before publishing.
        </p>

        <div v-if="isOpen" class="field-picker__dropdown">
            <input
                ref="inputRef"
                v-model="search"
                type="text"
                class="field-picker__search"
                placeholder="Search fields..."
                @blur="handleBlur"
            />
            <div class="field-picker__list">
                <div v-if="hasUnavailableFields" class="field-picker__notice">
                    Fields marked “Not available yet” aren’t collected for contacts yet, so they can’t be used in workflows.
                </div>
                <template v-for="group in filteredGroups" :key="group.category">
                    <div class="field-picker__category">{{ group.category }}</div>
                    <button
                        v-for="field in group.fields"
                        :key="field.key"
                        class="field-picker__item"
                        :class="{
                            'field-picker__item--selected': field.key === modelValue,
                            'field-picker__item--unavailable': isUnavailable(field),
                        }"
                        :disabled="isUnavailable(field)"
                        :aria-disabled="isUnavailable(field)"
                        type="button"
                        @mousedown.prevent="selectField(field)"
                    >
                        <span class="field-picker__icon">{{ typeIcons[field.type] || '?' }}</span>
                        <span class="field-picker__title">{{ field.title }}</span>
                        <span v-if="isUnavailable(field)" class="field-picker__badge">Not available yet</span>
                        <span v-else class="field-picker__key">{{ field.key }}</span>
                    </button>
                </template>
                <div v-if="filteredGroups.length === 0" class="field-picker__empty">
                    No fields match your search
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.field-picker {
    position: relative;
}
.field-picker__trigger {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    text-align: left;
    cursor: pointer;
    font-size: 13px;
    color: #111827;
}
.field-picker__trigger:hover {
    border-color: #9ca3af;
}
.field-picker__placeholder {
    color: #9ca3af;
}
.field-picker__dropdown {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
}
.field-picker__search {
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    outline: none;
    border-radius: 6px 6px 0 0;
    box-sizing: border-box;
}
.field-picker__list {
    max-height: 280px;
    overflow-y: auto;
}
.field-picker__category {
    padding: 6px 12px 2px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6b7280;
    background: #f9fafb;
    position: sticky;
    top: 0;
}
.field-picker__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    text-align: left;
}
.field-picker__item:hover {
    background: #f3f4f6;
}
.field-picker__item--selected {
    background: #eff6ff;
}
.field-picker__icon {
    flex-shrink: 0;
    width: 18px;
    text-align: center;
    font-size: 11px;
    color: #6b7280;
}
.field-picker__title {
    flex: 1;
    color: #111827;
}
.field-picker__key {
    font-size: 11px;
    color: #9ca3af;
    font-family: monospace;
}
.field-picker__item--unavailable {
    cursor: not-allowed;
}
.field-picker__item--unavailable:hover {
    background: none;
}
.field-picker__item--unavailable .field-picker__title,
.field-picker__item--unavailable .field-picker__icon {
    color: #9ca3af;
}
.field-picker__badge {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 10px;
    white-space: nowrap;
}
.field-picker__notice {
    padding: 8px 12px;
    font-size: 11px;
    line-height: 1.4;
    color: #92400e;
    background: #fffbeb;
    border-bottom: 1px solid #fde68a;
}
.field-picker__warning {
    margin: 4px 0 0;
    font-size: 11px;
    color: #b45309;
}
.field-picker__empty {
    padding: 12px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
}
</style>
